<!-- eslint-disable vue/no-v-html -->
<!-- eslint-disable vue/first-attribute-linebreak -->
<template>
  <div class="mobile-registration-form__privacy-policy">
    <a class="mobile-registration-form__privacy-policy-link"
       href="/home/privacy-policy"
       target="_blank"
       rel="noopener noreferrer">Privacy policy</a>
  </div>
  <div class="mobile-registration-form"
       :class="{ 'mobile-registration-form--verify': hasSentVerificationCode }">
    <UtdOptionsCard class="primary-card">
      <template #content>
        <div class="mobile-registration-form__container">
          <div class="mobile-registration-form__inner-container">
            <form @submit.prevent="handleFormSubmit">
              <UtdNotification v-if="isServerErrorNotificationVisible"
                               id="mobileServerErrorNotification"
                               :has-close-notification="true"
                               notification-mode="error"
                               @closed="isServerErrorNotificationVisible = false">
                <template #title>
                  <h4>Submission Error</h4>
                </template>
                <div class="mobile-registration-form__server-error-notification">
                  <p v-for="(error, key) in serverError.general"
                     :key="key"
                     v-html="error" />
                </div>
              </UtdNotification>

              <div v-show="!hasSentVerificationCode">
                <IapRegistrationFormBasicInfo @select-change="handleSelectChange" />
                <IapRegistrationFormAddressInfo @select-change="handleSelectChange" />
                <IapRegistrationFormPracticeType @select-change="handleSelectChange" />
                <IapRegistrationFormUsernamePassword
                  v-show="isShowUsernamePassword"
                  :has-sent-verification-code="hasSentVerificationCode"
                  :has-resent-verification-code="hasResentVerificationCode"
                  :is-auth-notification-visible="isAuthNotificationVisible"
                  :is-auth-error-notification-visible="isAuthErrorNotificationVisible"
                  :is-allow-otc-generation="isAllowOtcGeneration"
                  :is-submitting="isSubmitting"
                  @close-auth-error-notification="isAuthErrorNotificationVisible = false"
                  @send-verification-code="handleSendVerificationCode"
                  @submit-form="handleFormSubmit"
                />
              </div>

              <div v-if="hasSentVerificationCode"
                   class="mobile-registration-form__otp-section">
                <div class="mobile-registration-form__otp-header">
                  <h1 class="mobile-registration-form__otp-title">
                    Verify Email
                  </h1>
                  <p class="mobile-registration-form__otp-subtitle">
                    We just sent an email to<br>{{ emailLocalPart }}<wbr>@{{ emailDomainPart }}.
                  </p>
                </div>
                <div v-if="hasResentVerificationCode"
                     class="mobile-registration-form__resend-code-text">
                  <i class="wkce-icon-filled-check-circle" />
                  <p>A new code has been sent</p>
                </div>
                <div v-if="isAuthErrorNotificationVisible"
                     class="mobile-registration-form__two-factor-notification">
                  <UtdNotification id="mobileOtpErrorNotification"
                                   :has-close-notification="true"
                                   notification-mode="error"
                                   @closed="isAuthErrorNotificationVisible = false">
                    <template #title>
                      <h4>Submission Error</h4>
                    </template>
                    <template #listContent>
                      <div class="mobile-registration-form__auth-notification-list">
                        <p v-for="(message, index) in authErrorMessages"
                           :key="index"
                           class="mobile-registration-form__auth-notification-list-item"
                           v-html="message" />
                      </div>
                    </template>
                  </UtdNotification>
                </div>
                <div class="mobile-registration-form__otp-input-wrapper">
                  <UtdVerificationCodeInput ref="otpInputRef"
                                            v-model="form.verificationCode"
                                            :has-error="invalidVerificationCode() && isDirty.verificationCode"
                                            @submit="handleFormSubmit" />
                  <p class="mobile-registration-form__otp-hint">
                    Enter the 6 digit code that appears in your email
                  </p>
                  <p v-if="invalidVerificationCode() && isDirty.verificationCode"
                     class="mobile-registration-form__otp-error">
                    {{ $t(verificationCodeErrorMessage()) }}
                  </p>
                </div>
                <fieldset class="mobile-registration-form__resend-action wk-js2 mobile-registration-form-field">
                  <UtdButton id="mobileSubmitVerificationButton"
                             :disabled="isSubmitCodeDisabled"
                             button-color="green-shade-3"
                             button-size="large"
                             type="submit"
                             @click="handleFormSubmit">
                    Verify
                  </UtdButton>
                  <UtdButton id="mobileResendVerificationButton"
                             button-style="ghost"
                             button-size="large"
                             type="button"
                             :disabled="isSendNewCodeDisabled || !isAllowOtcGeneration || isSubmitting"
                             @click.prevent="handleSendVerificationCode({ resendCode: true })">
                    Re-send Email
                  </UtdButton>
                </fieldset>
              </div>
            </form>
          </div>
        </div>
      </template>
      <template #actions>
        <UtdButton v-if="!hasSentVerificationCode && !isRequiresTwoFactorValidation"
                   id="mobileSubmitButton"
                   :disabled="isSubmitDisabled || isSubmitting"
                   button-color="green-shade-3"
                   button-size="large"
                   type="submit"
                   @click="handleFormSubmit">
          {{ isInAppPurchase ? 'Register' : 'Submit' }}
        </UtdButton>
        <UtdButton v-else-if="!hasSentVerificationCode && isRequiresTwoFactorValidation"
                   id="mobileSendVerificationButton"
                   :disabled="isSubmitDisabled || isSubmitting"
                   button-color="green-shade-3"
                   button-size="large"
                   type="submit"
                   @click="handleSendVerificationCode">
          Register
        </UtdButton>

      </template>
    </UtdOptionsCard>
  </div>
</template>

<script setup>
import UtdOptionsCard from '_acaSrc/components/shared/stdlib/UtdOptionsCard.vue';
import UtdButton from '_acaSrc/components/shared/stdlib/UtdButton.vue';
import UtdTextInput from '_acaSrc/components/shared/input/UtdTextInput.vue';
import UtdNotification from '_acaSrc/components/shared/utd/UtdNotification.vue';
import UtdVerificationCodeInput from '_acaSrc/components/shared/stdlib/UtdVerificationCodeInput.vue';
import { getWindow } from '_acaSrc/utility/DOM';
import useRegistrationForm from '_acaSrc/composables/registrationForm.composable';
import { useStore } from 'vuex';
import { onMounted, ref, computed, watch, nextTick } from 'vue';
import {
    C_HTTP,
    GUEST_PASS_LICENSE,
    REG_FIELD,
    REGISTRATION_DEFAULT_ERROR_MESSAGE,
    REGISTRATION_FIELD_ERROR,
    REGISTRATION_FIELD_ERROR_MESSAGE,
    REGISTRATION_NO_OTC_ENTRY_ERROR_MESSAGE,
    REGISTRATION_OTC_EXIT_BROWSER_ERROR_MESSAGE,
    REGISTRATION_REVIEW_FORM_MESSAGE,
    SALUTATION_OPTIONS,
    UTD_TWO_FACTOR_AUTH_MESSAGES,
    UTD_TWO_FACTOR_AUTH_ERROR_MESSAGES
} from '_acaSrc/utility/constants';
import utdRest from '_acaSrc/utility/http/UtdRestHooks';
import IapRegistrationFormBasicInfo from './IapRegistrationFormBasicInfo.vue';
import IapRegistrationFormAddressInfo from './IapRegistrationFormAddressInfo.vue';
import IapRegistrationFormPracticeType from './IapRegistrationFormPracticeType.vue';
import IapRegistrationFormUsernamePassword from './IapRegistrationFormUsernamePassword.vue';
import DOMPurify from 'dompurify';

const MAX_VERIFICATION_CODE_LENGTH = 24;

const urlSearchParams = new URLSearchParams(window.location.search);
const isInAppPurchase = urlSearchParams.get('iap') === 'true';

const {
    form,
    isDirty,
    isUSOrCanada,
    cityDropdownOptions,
    filteredStateOptions,
    needsNewCode,
    zipCodePrefilled,
    isSubmitDisabled,
    sendCodeAttempts,
    resetServerError,
    checkResetServerError,
    serverError,
    setServerError,
    resetForm,
    validateForm,
    checkFieldInvalid,
    resetAllServerErrors,
    isSendNewCodeDisabled,
    invalidVerificationCode,
    verificationCodeErrorMessage
} = useRegistrationForm();

const store = useStore();

const isShowUsernamePassword = true;
const emailLocalPart = computed(() => form.email?.split('@')[0] ?? '');
const emailDomainPart = computed(() => form.email?.split('@')[1] ?? form.email);
const registrationInfo = computed(() => store.getters['login/registrationInfo']);

const isSubmitCodeDisabled = computed(() =>
    isSubmitDisabled.value
    || checkFieldInvalid(REG_FIELD.VERIFICATION_CODE)
    || !isAllowOtcEntry.value
    || needsNewCode.value
    || isSubmitting.value
);

const emit = defineEmits([ 'cancelRegistration', 'update:hasSentVerificationCode', 'update:userEmail' ]);

const stateDropdownList = computed(() => store.getters['account/myAccountStates']);
const handleSelectChange = ({ value, name }) => {
    if (name === REG_FIELD.COUNTRY) {
        form.zip = '';
        form.city = '';
        form.state = null;
        isDirty.zip = false;
        isDirty.city = false;
        zipCodePrefilled.value = false;
        cityDropdownOptions.value = [];
        filteredStateOptions.value = stateDropdownList.value;
        resetServerError(REG_FIELD.ZIP);
    }
    form[name] = parseInt(value);
    checkResetServerError();
};

const hasSentVerificationCode = ref(false);
const hasResentVerificationCode = ref(false);
const otpInputRef = ref(null);

watch(hasSentVerificationCode, val => {
    emit('update:hasSentVerificationCode', val);
    if (val) {
        emit('update:userEmail', form.email);
        nextTick(() => otpInputRef.value?.focus());
    }
});
const isAuthNotificationVisible = ref(false);
const isAuthErrorNotificationVisible = ref(false);
const isSubmitting = ref(false);

const setupFormPayload = (inputForm) => {
    const responseForm = {
        salutation: inputForm.salutation,
        firstName: inputForm.firstName,
        lastName: inputForm.lastName,
        suffix: inputForm.suffix,
        email: inputForm.email,
        city: inputForm.city,
        zip: inputForm.zip,
        state: inputForm.state,
        county: inputForm.county,
        address1: inputForm.address1,
        address2: inputForm.address2,
        phone1: inputForm.phone1,
        phone1Type: inputForm.phone1Type,
        phone1Ext: inputForm.phone1Ext,
        country: inputForm.country,
        practiceSetting: inputForm.practiceSetting,
        practiceType: inputForm.practiceType,
        specialty: inputForm.specialty,
        registrationUserName: inputForm.registrationUserName,
        password: inputForm.password,
        verifyPassword: inputForm.verifyPassword
    };
    transformDropdownValues(responseForm);
    setGuestPassUserFields(responseForm);
    return responseForm;
};

const isGuestPassUser = computed(() =>
    store.getters['footer/licensee'] === GUEST_PASS_LICENSE
    || store.getters['profile/userProfileVisitor'].isGuestUser);
const isCancelButtonVisible = computed(() => !isGuestPassUser.value);

const setGuestPassUserFields = (responseForm) => {
    if (isGuestPassUser.value) {
        responseForm.registrationUserName =
            store.getters['profile/userProfileVisitor'].userName || '';
    }
};

const transformDropdownValues = (responseForm) => {
    const selectedCountryIdx = parseInt(form.country);
    const formattedCountry = store.getters['account/myAccountCountries'][selectedCountryIdx]?.value || '';
    responseForm.country = formattedCountry;

    const selectedSalutationIdx = parseInt(form.salutation);
    const formattedSalutation = SALUTATION_OPTIONS[selectedSalutationIdx]?.value || '';
    responseForm.salutation = formattedSalutation;

    if (isUSOrCanada.value) {
        transformCityStateDropdownValues(responseForm);
    }
};

const transformCityStateDropdownValues = (responseForm) => {
    if (isUSOrCanada.value) {
        const selectedCityIdx = parseInt(form.city);
        const formattedCity = cityDropdownOptions.value[selectedCityIdx]?.label || '';
        responseForm.city = formattedCity;
    }
    const stateOptions = filteredStateOptions.value?.length
        ? filteredStateOptions.value
        : stateDropdownList.value;
    const selectedStateIdx = parseInt(form.state);
    const formattedState = stateOptions[selectedStateIdx]?.value || '';
    responseForm.state = formattedState;
};

const isServerErrorNotificationVisible = ref(false);

const handleGeneralServerError = (error) => {
    if (!error) {
        return;
    }
    handleOtcState(error);
    if (error.error?.rawResponse) {
        handleRawErrorResponse(error.error.rawResponse);
    }
    else if (error.data) {
        handleErrorData(error.data);
    }
    else {
        setServerError('general', REGISTRATION_DEFAULT_ERROR_MESSAGE);
    }
    handleNotificationVisibility();
};

const handleErrorData = (response) => {
    if (!response) {
        return;
    }
    if (response.errorMessage) {
        setServerError(REG_FIELD.VERIFICATION_CODE, response.errorMessage);
        setServerError('general', REGISTRATION_FIELD_ERROR_MESSAGE);
        isAuthNotificationVisible.value = false;
    }
    else if (response.formFields
          || response.statusMessage === REGISTRATION_REVIEW_FORM_MESSAGE) {
        setServerError('general', REGISTRATION_FIELD_ERROR_MESSAGE);
        handleResponseIsFailure(response);
    }
};

const isAllowOtcGeneration = ref(true);
const isAllowOtcEntry = ref(true);
const handleOtcState = (error) => {
    if (typeof error?.data?.isAllowOtcGeneration !== 'boolean') {
        return;
    }
    isAllowOtcGeneration.value = error.data.isAllowOtcGeneration;
    if (typeof error?.data?.isAllowOtcEntry !== 'boolean') {
        return;
    }
    isAllowOtcEntry.value = error.data.isAllowOtcEntry;
    setOtcErrorMessages();
};

const setOtcErrorMessages = () => {
    if (!isAllowOtcEntry.value) {
        setServerError('twoFactor', REGISTRATION_NO_OTC_ENTRY_ERROR_MESSAGE);
        isAuthErrorNotificationVisible.value = true;
    }
    else {
        checkResetServerError('twoFactor');
    }
    if (!isAllowOtcEntry.value && !isAllowOtcGeneration.value) {
        setServerError('twoFactor', REGISTRATION_OTC_EXIT_BROWSER_ERROR_MESSAGE);
        isAuthErrorNotificationVisible.value = true;
    }
};

const isRequiresTwoFactorValidation = computed(() =>
    isShowUsernamePassword && registrationInfo.value.isTwoFactorEnabled);

const handleNotificationVisibility = () => {
    if (isRequiresTwoFactorValidation.value) {
        isAuthErrorNotificationVisible.value = true;
    }
    isServerErrorNotificationVisible.value = true;
};

const handleRawErrorResponse = (error) => {
    isAuthNotificationVisible.value = false;
    setServerError('general', REGISTRATION_FIELD_ERROR_MESSAGE);
    if (error.includes(REG_FIELD.EMAIL)) {
        setServerError(REG_FIELD.EMAIL, REGISTRATION_FIELD_ERROR.INCORRECT_EMAIL);
        needsNewCode.value = true;
    }
    else if (error.includes('code')) {
        setServerError(REG_FIELD.VERIFICATION_CODE, REGISTRATION_FIELD_ERROR.INVALID_CODE);
    }
};

const authErrorMessages = computed(() => {
    const errorMessages = [];
    if (serverError.twoFactor) {
        errorMessages.push(...serverError.twoFactor);
    }
    else {
        errorMessages.push(...UTD_TWO_FACTOR_AUTH_ERROR_MESSAGES);
    }
    return errorMessages;
});

const handleSendVerificationCode = async({ resendCode = false } = {}) => {
    if (isSubmitting.value) {
        return;
    }
    isSubmitting.value = true;
    const sanitizedForm = sanitizeForm({ ...form });
    if (!validateForm()) {
        isSubmitDisabled.value = true;
        isSubmitting.value = false;
        return;
    }
    if (resendCode) {
        hasResentVerificationCode.value = true;
        isAllowOtcEntry.value = true;
        checkResetServerError(REG_FIELD.VERIFICATION_CODE);
    }
    needsNewCode.value = false;
    resetAllServerErrors();
    const responseForm = setupFormPayload(sanitizedForm);
    try {
        const response = await utdRest('register/two-factor-code', {
            formName: 'INTERNATIONAL',
            ...responseForm
        });
        if (response.isSuccess) {
            isAuthErrorNotificationVisible.value = false;
            isServerErrorNotificationVisible.value = false;
            hasSentVerificationCode.value = true;
            isAuthNotificationVisible.value = true;
            sendCodeAttempts.value += 1;
        }
        else {
            handleResponseIsFailure(response);
        }
    }
    catch (error) {
        handleGeneralServerError(error);
    }
    finally {
        isSubmitting.value = false;
    }
};

const handleResponseIsFailure = (response) => {
    parseServerFieldErrors(response);
    setServerError('general', REGISTRATION_FIELD_ERROR_MESSAGE);
    isAuthErrorNotificationVisible.value = true;
    hasSentVerificationCode.value = false;
    isAuthNotificationVisible.value = false;
    isSubmitDisabled.value = true;
    isServerErrorNotificationVisible.value = true;
};

const handleFormSubmit = async() => {
    if (isSubmitting.value) {
        return;
    }
    isSubmitting.value = true;
    const sanitizedForm = sanitizeForm({ ...form });
    if (!validateForm()) {
        isSubmitting.value = false;
        return;
    }
    const responseForm = setupFormPayload(sanitizedForm);
    try {
        const response = await utdRest('register/submit', {
            form: {
                formName: 'INTERNATIONAL',
                ...responseForm
            },
            twoFactorCode: form.verificationCode,
            termsAccepted: true
        });
        handleSubmitResponse(response);
    }
    catch (error) {
        handleGeneralServerError(error);
    }
    finally {
        isSubmitting.value = false;
    }
};

const handleSubmitResponse = (response) => {
    if (!response.isSuccess) {
        parseServerFieldErrors(response);
        setServerError('general', REGISTRATION_FIELD_ERROR_MESSAGE);
        handleNotificationVisibility();
    }
};

const sanitizeForm = (requestForm) => {
    Object.keys(requestForm).forEach((key) => {
        requestForm[key] = DOMPurify.sanitize(requestForm[key]);
    });
    return requestForm;
};

const parseServerFieldErrors = (response) => {
    const fields = response?.formFields || [];
    fields.forEach((field) => {
        if (field.errorMessage) {
            setServerError(field.name, field.errorMessage);
        }
    });
};

const isOidcUser = computed(() => store.getters['auth/isOidcUser']);
const isAuthenticatedByIp = computed(() => store.getters['app/isAuthenticatedByIp']);
const router = computed(() => store.getters['app/router']);

const cancelRegistration = () => {
    resetForm();
    if (isOidcUser.value || isAuthenticatedByIp.value) {
        router.value.url(C_HTTP.DEFAULT_HOMEPAGE_PATH);
        return;
    }
    getWindow().scrollTo(0, 0);
    emit('cancelRegistration');
};

const setMobileFormDefaults = () => {
    form.termsAccepted = true;
    const HIDDEN_REQUIRED_SELECTS = [
        REG_FIELD.SALUTATION,
        REG_FIELD.PRACTICE_SETTING,
        REG_FIELD.PRACTICE_TYPE,
        REG_FIELD.SPECIALTY
    ];
    HIDDEN_REQUIRED_SELECTS.forEach(field => {
        if (form[field] === null) {
            form[field] = 0;
        }
    });
    checkResetServerError();
};

const trackRegisterBeginEvent = async() => {
    try {
        await utdRest('register/begin');
    }
    catch {
        // No-op
    }
};

onMounted(() => {
    setMobileFormDefaults();
    trackRegisterBeginEvent();
});
</script>

<style lang="less" scoped>
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';
@DS1-UTD-ERROR-ON-BLUE-BG-COLOR: @WKCE-RED-SHADE1;

.mobile-registration-form__server-error-notification,
.mobile-registration-form__auth-notification-list {
  p {
    margin: 0;
    padding: 0;
    line-height: 1.5;

    & + p {
      margin-top: 4px;
    }
  }
}

@media only screen and (min-width: 768px) {
  .mobile-registration-form__container {
    .ds1-ph-2();
  }
  .mobile-registration-form {
    :deep(.options-card .options-card__content) {
      .ds1-pb-4();
    }
    :deep(.options-card__content .options-card__action-area) {
      .ds1-ph-5();
    }
    :deep(.mobile-registration-form__subtitle) {
      .ds1-mh-0();
      .ds1-mt-3();
      .ds1-utd-h4();
    }
  }
}

.mobile-registration-form {
  .ds1-mt-2();
  width: 100%;

  &.mobile-registration-form--verify {
    margin-top: 0;

    :deep(.mobile-registration-form__container) {
      margin-top: 0;
      padding-top: 0;
      padding-bottom: 120px;
    }

    :deep(.mobile-registration-form__resend-action) {
      position: fixed;
      bottom: 24px;
      left: 50%;
      transform: translateX(-50%);
      width: calc(100% - 32px);
      max-width: 690px;
      margin-top: 0;
      padding: 0;
      z-index: 10;
    }
  }

  .wk-js2 .wk-field-errored .wk-field-input:not(:hover :focus) {
    border-color: @DS1-UTD-ERROR-ON-BLUE-BG-COLOR;
  }

  :deep(.mobile-registration-form-field) {
    .ds1-ph-1();
    .ds1-ma-0();
    border: none;
  }

  :deep(.wk-field-input.form-field) {
    background-color: @DS1-UTD-PRIMARY-BG-COLOR;
  }

  :deep(.wk-js2 .wk-field-error) {
    color: @DS1-UTD-ERROR-ON-BLUE-BG-COLOR;
  }

  :deep(.options-card__action-area) {
    .ds1-pr-1();
    gap: 24px;
  }

  :deep(.options-card__header) {
    display: block;
  }
}

.mobile-registration-form__server-error-notification {
  ul {
    .ds1-pl-3();
    .ds1-mb-0();
    .ds1-mt-1();
  }
}

#mobileServerErrorNotification {
  .ds1-mh-3();
  .ds1-mb-2();
}

.mobile-registration-form__privacy-policy {
  .ds1-mt-2();
  .ds1-mb-2();
  align-self: flex-start;

  .mobile-registration-form__privacy-policy-link {
    color: @DS1-UTD-PRIMARY-LINK-BASE-COLOR;

    &:hover {
      color: @WKCE-BLUE-SHADE1;
      text-decoration: underline;
    }
  }
}

.mobile-registration-form__otp-section {
  .ds1-ph-1();
  margin-top: 0;
  padding-top: 0;
}

.mobile-registration-form__otp-header {
  text-align: center;
  .ds1-pt-3();
  .ds1-ph-2();
  .ds1-pb-2();
}

.mobile-registration-form__otp-title {
  margin: 0 0 20px;
  font-size: 32px;
  font-weight: 700;
  color: @WKCE-GRAY-SHADE2;
}

.mobile-registration-form__otp-subtitle {
  margin: 0;
  font-size: 16px;
  line-height: 1.5;
  color: @WKCE-GRAY-SHADE2;
  overflow-wrap: break-word;
}

.mobile-registration-form__two-factor-notification {
  .ds1-ph-1();
  .ds1-mb-2();
}

.mobile-registration-form__otp-input-wrapper {
  .ds1-mb-3();
  margin-top: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.mobile-registration-form__otp-hint {
  margin: 0;
  font-size: 14px;
  color: @WKCE-GRAY-SHADE1;
  text-align: center;
}

.mobile-registration-form__otp-error {
  margin: 0;
  font-size: 13px;
  color: @WKCE-RED;
  text-align: center;
}

.mobile-registration-form__auth-notification-list {
  .ds1-mv-1();
  .ds1-pl-3();
  width: 85%;
  color: @DS1-UTD-TOPIC-TEXT-COLOR;
}

.mobile-registration-form__auth-notification-list-item {
  margin-bottom: 4px;
}

.mobile-registration-form__resend-code-text {
  .ds1-ma-1();
  .ds1-flex-vertical-center();
  .ds1-utd-weight-600();
  gap: 4px;
  font-size: 16px;
}

.wkce-icon-filled-check-circle {
  color: @DS1-UTD-SUCCESS;
  font-size: 20px;
}

.mobile-registration-form__resend-action {
  display: flex;
  flex-direction: column;
  gap: 8px;

  #mobileResendVerificationButton {
    border-radius: 50px;
    width: 100%;
  }
}

.xAIMobile {
  .mobile-registration-form__privacy-policy {
    display: none;
  }

  .mobile-registration-form__privacy-policy-link {
    color: @DS1-UTD-PRIMARY-LINK-BASE-COLOR;

    &:hover {
      color: @WKCE-BLUE-SHADE1;
    }
  }

  .mobile-registration-form {
    :deep(.wk-field-input.form-field) {
      background-color: @WKCE-WHITE;
      border-radius: 50px;
      border: 1px solid @WKCE-CREAM;
      font-size: 16px;
      font-weight: 300;
      text-overflow: ellipsis;
    }

    :deep(.wk-field-input.form-field:focus) {
      border-color: @WKCE-CREAM-SHADE1;
    }

    :deep(.wk-field-label) {
      color: @WKCE-CREAM-SHADE2;
      font-size: 14px;
      font-weight: 600;
    }

    :deep(.options-card) {
      background-color: @WKCE-CREAM-TINT2;
      border: none;
      box-shadow: none;
      border-radius: 12px;
    }

    :deep(.options-card__action-area) {
      background-color: @WKCE-CREAM-TINT2;
      justify-content: center;
    }

    &.mobile-registration-form--verify {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      overflow: hidden;
      margin-top: 0;

      :deep(.mobile-registration-form__container) {
        margin-top: 0;
        padding-top: 0;
      }

      :deep(.options-card),
      :deep(.primary-card) {
        background-color: @WKCE-WHITE;
        border-radius: 0;
        border: none;
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        overflow: hidden;
      }

      :deep(.options-card__content) {
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        padding: 0;
      }

      :deep(.options-card__action-area) {
        background-color: @WKCE-WHITE;
        display: none;
      }

      :deep(.mobile-registration-form__container),
      :deep(.mobile-registration-form__inner-container),
      :deep(.mobile-registration-form__inner-container > form) {
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
      }

      :deep(.mobile-registration-form__otp-section) {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        margin-top: 0;
        padding: 0 24px;
        align-self: stretch;
        flex: none;
      }

      :deep(.mobile-registration-form__otp-header) {
        flex-shrink: 0;
        padding: 48px 0 0;
      }

      :deep(.mobile-registration-form__otp-input-wrapper) {
        flex-shrink: 0;
        margin-top: 32px;
        margin-bottom: 0;
        gap: 16px;
      }

      :deep(.mobile-registration-form__resend-action) {
        margin-top: auto;
        padding: 16px 0 32px;
      }
    }

    :deep(.options-card__header) {
      h2, h3, h4, p, span {
        color: @WKCE-CREAM-SHADE2;
      }
    }

    :deep(.utd-button-size-large) {
      border-radius: 50px;
      width: 100%;
    }

    :deep(.utd-button-color-blue.utd-button-size-large) {
      background-color: @WKCE-CREAM-SHADE2;
      border-color: @WKCE-CREAM-SHADE2;

      &:hover {
        background-color: @WKCE-CREAM-SHADE1;
        border-color: @WKCE-CREAM-SHADE1;
      }
    }

    :deep(.utd-button-color-green-shade-3.utd-button-size-large) {
      background-color: @WKCE-CREAM-SHADE1;

      &:hover {
        background-color: @WKCE-CREAM-SHADE2;
        border-color: @WKCE-CREAM-SHADE2;
      }
    }

    :deep(.utd-button-style-ghost.utd-button-size-large) {
      color: @WKCE-CREAM-SHADE1;
      border-color: @WKCE-CREAM-SHADE1;
      background-color: transparent;

      &:hover {
        background-color: @WKCE-CREAM-SHADE1;
        color: @WKCE-WHITE;
      }
    }
  }
}
</style>

